/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\TemplateFormFields.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the email-templates module.
----------------------------------------------------------------*/

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Toggle } from '../../../components/ui/Toggle';
import { Mail, Settings, Code, Variable, FileCode } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { EmailTemplate } from '../api/emailTemplatesApi';
import { Badge } from '../../../components/ui/badge';
import { Editor } from '@tinymce/tinymce-react';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import axios from 'axios';
import { useAuthStore } from '../../../store/auth';
import { buildApiUrl, getApiHeaders } from '../../../config/api';
import { useThemeStore } from '../../../store/theme';

interface TemplateFormFieldsProps {
    formData: Partial<EmailTemplate>;
    errors: Record<string, string>;
    onInputChange: (field: keyof EmailTemplate, value: unknown) => void;
    mode: 'create' | 'edit';
}

export function TemplateFormFields({ formData, errors, onInputChange, mode }: TemplateFormFieldsProps) {
    const { t } = useI18n();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [editorInstance, setEditorInstance] = useState<any>(null);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleEditorInit = (_evt: unknown, editor: any) => {
        setEditorInstance(editor);
    };

    const variablesRef = useRef<{ name: string; description: string }[]>([]);
    useEffect(() => {
        variablesRef.current = (formData.variables as any) || [];
    }, [formData.variables]);

    const insertVariable = useCallback((variable: string) => {
        const variableTag = `\${${variable}}`;
        if (editorInstance) {
            editorInstance.insertContent(variableTag);
            editorInstance.focus();
        } else {
            const currentHtml = formData.htmlContent || '';
            onInputChange('htmlContent', currentHtml + variableTag);
        }
    }, [editorInstance, formData.htmlContent, onInputChange]);

    const isDarkMode = document.documentElement.classList.contains('dark');

    return (
        <div className="space-y-4">
            <FormBox title={t('emailTemplates.sections.basicInfo')} icon={<Mail className="h-5 w-5" />}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-start">
                    {/* Row 1: Name & Enabled */}
                    <SimpleTooltip content={t('emailTemplates.fields.name.tooltip')}>
                        <Label htmlFor="name" className="text-left font-medium">
                            {t('emailTemplates.fields.name.label')}
                        </Label>
                    </SimpleTooltip>
                    <div className="col-span-1">
                        <Input
                            id="name"
                            value={formData.name || ''}
                            onChange={(e) => onInputChange('name', e.target.value)}
                            placeholder={t('emailTemplates.fields.name.placeholder')}
                            className={errors.name ? 'border-red-500' : ''}
                        />
                        {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                    </div>

                    <SimpleTooltip content={t('emailTemplates.fields.isActive.tooltip')}>
                        <Label htmlFor="isActive" className="text-left font-medium">
                            {t('common.enabled')}
                        </Label>
                    </SimpleTooltip>
                    <div className="col-span-1">
                        <Toggle
                            id="isActive"
                            value={formData.isActive || false}
                            onChange={(checked) => onInputChange('isActive', checked)}
                        />
                    </div>

                    {/* Row 2: From Email & From Name */}
                    <SimpleTooltip content={t('emailTemplates.fields.fromEmail.tooltip')}>
                        <Label htmlFor="fromEmail" className="text-left font-medium">
                            {t('emailTemplates.fields.fromEmail.label')}
                        </Label>
                    </SimpleTooltip>
                    <div className="col-span-1">
                        <Input
                            id="fromEmail"
                            value={formData.fromEmail || ''}
                            onChange={(e) => onInputChange('fromEmail', e.target.value)}
                            placeholder={t('emailTemplates.fields.fromEmail.placeholder')}
                            className={errors.fromEmail ? 'border-red-500' : ''}
                        />
                        {errors.fromEmail && <p className="text-xs text-red-500 mt-1">{errors.fromEmail}</p>}
                    </div>

                    <SimpleTooltip content={t('emailTemplates.fields.fromName.tooltip')}>
                        <Label htmlFor="fromName" className="text-left font-medium">
                            {t('emailTemplates.fields.fromName.label')}
                        </Label>
                    </SimpleTooltip>
                    <div className="col-span-1">
                        <Input
                            id="fromName"
                            value={formData.fromName || ''}
                            onChange={(e) => onInputChange('fromName', e.target.value)}
                            placeholder={t('emailTemplates.fields.fromName.placeholder')}
                        />
                    </div>

                    {/* Row 3: Subject */}
                    <SimpleTooltip content={t('emailTemplates.fields.subject.tooltip')}>
                        <Label htmlFor="subject" className="text-left font-medium">
                            {t('emailTemplates.fields.subject.label')}
                        </Label>
                    </SimpleTooltip>
                    <div className="col-span-3">
                        <Input
                            id="subject"
                            value={formData.subject || ''}
                            onChange={(e) => onInputChange('subject', e.target.value)}
                            placeholder={t('emailTemplates.fields.subject.placeholder')}
                            className={errors.subject ? 'border-red-500' : ''}
                        />
                        {errors.subject && <p className="text-xs text-red-500 mt-1">{errors.subject}</p>}
                    </div>
                </div>
            </FormBox>

            <FormBox
                title={t('emailTemplates.sections.content')}
                icon={<Code className="h-5 w-5" />}
            >
                <div className="space-y-4">
                    <div className="space-y-2">
                        <div className="min-h-[500px] border border-border rounded-lg overflow-hidden relative shadow-sm">
                            {/* @ts-ignore - TinyMCE Editor type incompatibility with React 19 */}
                            <Editor
                                tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/6.8.2/tinymce.min.js"
                                onInit={handleEditorInit}
                                value={formData.htmlContent || ''}
                                onEditorChange={(content) => onInputChange('htmlContent', content)}
                                init={{
                                    height: 500,
                                    menubar: true,
                                    plugins: [
                                        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                        'insertdatetime', 'media', 'table', 'help', 'wordcount', 'emoticons'
                                    ],
                                    toolbar: 'undo redo | blocks | ' +
                                        'bold italic forecolor | alignleft aligncenter ' +
                                        'alignright alignjustify | bullist numlist outdent indent | ' +
                                        'removeformat | variables | image emoticons | help',
                                    content_style: 'body { font-family:Inter,Helvetica,Arial,sans-serif; font-size:14px; background-color: #ffffff; color: #333333; }',
                                    skin: isDarkMode ? 'oxide-dark' : 'oxide',
                                    content_css: 'default',
                                    branding: false,
                                    promotion: false,
                                    removed_menuitems: 'newdocument',
                                    relative_urls: false,
                                    remove_script_host: false,
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    images_upload_handler: (blobInfo: any) => {
                                        return new Promise((resolve, reject) => {
                                            const tenantSlug = useAuthStore.getState().user?.tenant?.slug || 'main';
                                            const headers = getApiHeaders(tenantSlug);
                                            // Remove Content-Type to let the browser set it for FormData
                                            delete (headers as any)['Content-Type'];

                                            const fd = new FormData();
                                            fd.append('file', blobInfo.blob(), blobInfo.filename());

                                            axios.post(buildApiUrl('/api/email-templates/upload'), fd, {
                                                headers
                                            })
                                                .then(response => {
                                                    // Prepend API base URL if the returned path is relative
                                                    const location = response.data.location;
                                                    const fullUrl = location.startsWith('http')
                                                        ? location
                                                        : `${buildApiUrl('')}${location}`;
                                                    resolve(fullUrl);
                                                })
                                                .catch(error => {
                                                    reject({ message: 'Error uploading image: ' + (error.response?.data?.message || error.message), remove: true });
                                                });
                                        });
                                    },
                                    setup: (editor) => {
                                        editor.ui.registry.addMenuButton('variables', {
                                            text: t('emailTemplates.sections.variablesShort'),
                                            fetch: (callback) => {
                                                const items = (variablesRef.current).map(variable => ({
                                                    type: 'menuitem',
                                                    text: `${variable.description} (\${${variable.name}})`,
                                                    onAction: () => editor.insertContent(`\${${variable.name}}`)
                                                }));
                                                callback(items);
                                            }
                                        });
                                    }
                                }}
                            />
                        </div>
                        {errors.htmlContent && <p className="text-xs text-red-500 mt-1">{errors.htmlContent}</p>}
                    </div>
                </div>
            </FormBox>
        </div>
    );
}
